import { authorizeExpiryEpochSec, DEFAULT_TOKEN_LIFETIME_SEC } from './authorize.ts';
import { scopesAllow, type AuthScope } from './scopes.ts';
import { authTokenExpired, type AuthTokenClaims } from './token.ts';

/**
 * Silent token renewal (2026-08-02) — the opt-in sixth scope and the pure half of the loop.
 *
 * The instance's token endpoints were measured before this was designed (API reference §8e), and
 * two findings decide the shape:
 *
 * - **`POST /api/v1/auth/tokens/register` mints only what the caller already holds.** A scope in
 *   the body the calling token does not carry is *silently dropped* — asking for `:*` answers
 *   `200` with `scopes: []` — so a renewal cannot escalate, and a renewal body must ask for
 *   exactly the held scopes or the successor quietly loses powers.
 * - **`POST /api/v1/auth/tokens/unregister` is self-only.** Even holding `POST:tokens*`, revoking
 *   any *other* session answers `400 Cannot revoke session …`; only `{session: <own>}` under that
 *   session's own bearer answers `204`. So the scope does **not** carry the power to end other
 *   sessions — the honest risk of granting it is that a leaked token can mint successors within
 *   its own scopes indefinitely, which is why it stays opt-in on the sign-in page rather than
 *   joining `VISIOUS_SCOPES`.
 *
 * The renewal itself: register a successor (same scopes, fresh 30-day expiry), store it, then
 * self-revoke the predecessor under its own bearer — best-effort, since an unrevoked predecessor
 * merely ages out.
 */

/** The opt-in scope. `POST` alone: register and unregister are both `POST`, and `GET:tokens` (the
 *  list of every session) is deliberately not asked for — renewal needs no inventory. */
export const TOKEN_RENEWAL_SCOPE: AuthScope = { methods: ['POST'], endpoint: 'tokens', wildcard: true };

/** The sign-in ask with renewal chosen: R9's enumerated list plus the one scope above. */
export function withRenewalScope(base: readonly AuthScope[]): readonly AuthScope[] {
  return [...base, TOKEN_RENEWAL_SCOPE];
}

export const TOKEN_REGISTER_PATH = '/api/v1/auth/tokens/register';
export const TOKEN_UNREGISTER_PATH = '/api/v1/auth/tokens/unregister';

/**
 * Renew inside the last week of a 30-day life: early enough that a weekly-use device still catches
 * the window, and comfortably before {@link SESSION_EXPIRY_WARNING_SEC} — a session that can renew
 * itself should be renewed before it would have started warning.
 */
export const TOKEN_RENEWAL_WINDOW_SEC = 7 * 24 * 60 * 60;

/** Whether this grant can mint its own successor. */
export function canSelfRenew(scopes: readonly AuthScope[]): boolean {
  return scopesAllow(scopes, 'POST', 'tokens/register');
}

/**
 * Act now? Holds the scope, is inside the window, and is not already dead — an *expired* token
 * cannot authenticate the register call, so past expiry the consent flow is the only way back.
 */
export function shouldRenewToken(claims: AuthTokenClaims, nowEpochSec: number): boolean {
  if (!canSelfRenew(claims.scopes)) return false;
  if (authTokenExpired(claims, nowEpochSec)) return false;
  return (
    claims.expireEpochSec !== null &&
    claims.expireEpochSec - nowEpochSec <= TOKEN_RENEWAL_WINDOW_SEC
  );
}

/**
 * The register body. `rawScopes` **verbatim** — a scope string core could not parse is still a
 * grant, and re-spelling the parsed ones would silently drop it from the successor. `expire` is an
 * absolute epoch: the endpoint stores the value as-is, exactly like `/authorize_token`
 * (`expire: 3600` mints a token that died in 1970 — measured on both).
 */
export function tokenRegisterBody(
  claims: AuthTokenClaims,
  nowEpochSec: number,
): Readonly<Record<string, unknown>> {
  return {
    scopes: [...claims.rawScopes],
    expire: authorizeExpiryEpochSec(nowEpochSec, DEFAULT_TOKEN_LIFETIME_SEC),
  };
}

/**
 * Is the register response a usable successor? The endpoint answers `200` with the token JSON as
 * the whole body, and the two failure shapes worth guarding are measured rather than imagined: a
 * body whose scopes were silently stripped (a successor that cannot renew again — or do anything),
 * and the verbatim-`expire` trap above (a successor already expired on arrival).
 */
export function renewedClaimsUsable(claims: AuthTokenClaims, nowEpochSec: number): boolean {
  return !authTokenExpired(claims, nowEpochSec) && canSelfRenew(claims.scopes);
}
